import { useEffect } from 'react';
import {
  // File menu
  FilePlus,
  FolderOpen,
  Cloud,
  Save,
  X,
  // Edit
  Scissors,
  Copy,
  ClipboardPaste,
  // History + view
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  Maximize,
  Grid3x3,
  HelpCircle,
  // Tool palette
  MousePointer2,
  Plus,
  Crop,
  Pencil,
  Type,
  Square,
  Eraser,
  PaintBucket,
  Sparkles,
  WandSparkles,
  // Transform
  RotateCcw,
  RotateCw,
  FlipHorizontal2,
  FlipVertical2,
  // Layers
  Eye,
  Trash2,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';

interface IconLegendDialogProps {
  onClose: () => void;
}

interface IconEntry {
  icon: React.ComponentType<{ className?: string }>;
  name: string;
  description: string;
}

interface IconGroup {
  title: string;
  entries: IconEntry[];
}

/** Exported for tests so a future lucide-react upgrade that renames an icon
 *  can be caught by an import-time assertion before it would render
 *  silently as `undefined`. */
export const ICON_LEGEND_GROUPS: IconGroup[] = [
  {
    title: 'File menu (folder icon, top-left)',
    entries: [
      { icon: FilePlus, name: 'New', description: 'Start a fresh blank canvas in a new tab.' },
      {
        icon: FolderOpen,
        name: 'Open Local',
        description:
          'Open an image from your device. Supports overwrite-in-place save in Chrome / Edge.',
      },
      {
        icon: Cloud,
        name: 'Open Fias',
        description: 'Open an image previously saved to Fias storage.',
      },
      {
        icon: Save,
        name: 'Save',
        description:
          'Save back to wherever the file was opened from (Fias re-upload, or local file overwrite when supported).',
      },
      {
        icon: Save,
        name: 'Save As…',
        description: 'Open the export dialog to choose name, format, dimensions, and destination.',
      },
      {
        icon: X,
        name: 'Close',
        description: 'Close the active tab. Prompts to save when there are unsaved changes.',
      },
    ],
  },
  {
    title: 'Edit (top toolbar)',
    entries: [
      {
        icon: Scissors,
        name: 'Cut (Ctrl+X)',
        description:
          'Cut the marqueed pixels from the active layer to the clipboard. Disabled until a marquee is finalized.',
      },
      {
        icon: Copy,
        name: 'Copy (Ctrl+C)',
        description: 'Copy the marqueed pixels to the clipboard without removing them.',
      },
      {
        icon: ClipboardPaste,
        name: 'Paste (Ctrl+V)',
        description: 'Paste the clipboard contents as a new layer at the original position.',
      },
    ],
  },
  {
    title: 'History (top toolbar)',
    entries: [
      {
        icon: Undo2,
        name: 'Undo (Ctrl+Z)',
        description: 'Step backward through your edit history.',
      },
      {
        icon: Redo2,
        name: 'Redo (Ctrl+Shift+Z)',
        description: 'Step forward through your edit history.',
      },
    ],
  },
  {
    title: 'View (top toolbar)',
    entries: [
      { icon: ZoomOut, name: 'Zoom out', description: 'Decrease zoom level.' },
      { icon: ZoomIn, name: 'Zoom in', description: 'Increase zoom level.' },
      {
        icon: Maximize,
        name: 'Fit to screen',
        description: 'Resize the canvas view to fit the editor area.',
      },
      {
        icon: Grid3x3,
        name: 'Toggle transparency grid',
        description: 'Show / hide the checkerboard background that reveals transparent pixels.',
      },
      { icon: HelpCircle, name: 'Help / icon legend', description: 'Open this dialog.' },
    ],
  },
  {
    title: 'Tools (left palette)',
    entries: [
      {
        icon: MousePointer2,
        name: 'Move (V)',
        description: 'Select and move objects on the canvas.',
      },
      {
        icon: Plus,
        name: 'Select / Marquee (M)',
        description: 'Make a selection (rectangle, ellipse, lasso, polygon, or wand).',
      },
      { icon: Crop, name: 'Crop (C)', description: 'Crop the canvas to a chosen region.' },
      {
        icon: Pencil,
        name: 'Draw (B)',
        description: 'Free-hand brush. Strokes bake into the active layer.',
      },
      {
        icon: Type,
        name: 'Text (T)',
        description: 'Add editable text. Click empty canvas to drop new text.',
      },
      {
        icon: Square,
        name: 'Shape (U)',
        description: 'Add a rectangle, circle, triangle, or line.',
      },
      {
        icon: Eraser,
        name: 'Eraser (E)',
        description: 'Erase pixels on the active layer (true erase, not paint white).',
      },
      {
        icon: PaintBucket,
        name: 'Fill (G)',
        description: 'Flood-fill color regions. Respects an active marquee.',
      },
      {
        icon: Sparkles,
        name: 'AI (A)',
        description:
          'Smart Delete: marquee an object, click the button, the AI removes it and fills from the surroundings (runs in-browser, no upstream cost). Smart Create: type a prompt, then either Blend (mask-aware inpaint into the marquee) or Insert (generate the subject as a new editable layer with its background removed).',
      },
    ],
  },
  {
    title: 'Actions (left palette, below tools)',
    entries: [
      {
        icon: WandSparkles,
        name: 'Remove background',
        description: 'AI-assisted background removal on the bg image.',
      },
    ],
  },
  {
    title: 'Transform (Move tool properties)',
    entries: [
      {
        icon: RotateCcw,
        name: 'Rotate left 90°',
        description: 'Rotate the entire canvas counter-clockwise.',
      },
      {
        icon: RotateCw,
        name: 'Rotate right 90°',
        description: 'Rotate the entire canvas clockwise.',
      },
      {
        icon: FlipHorizontal2,
        name: 'Flip horizontal',
        description: 'Mirror the canvas left ↔ right.',
      },
      {
        icon: FlipVertical2,
        name: 'Flip vertical',
        description: 'Mirror the canvas top ↔ bottom.',
      },
    ],
  },
  {
    title: 'Layers panel',
    entries: [
      {
        icon: Plus,
        name: 'New layer',
        description: 'Add a fresh empty layer above the current one.',
      },
      {
        icon: Eye,
        name: 'Toggle visibility',
        description: 'Show or hide a layer without deleting it.',
      },
      {
        icon: ChevronUp,
        name: 'Move layer up',
        description: 'Bring the layer one step toward the front.',
      },
      {
        icon: ChevronDown,
        name: 'Move layer down',
        description: 'Send the layer one step toward the back.',
      },
      { icon: Trash2, name: 'Delete layer', description: 'Remove a layer and everything on it.' },
    ],
  },
];

/** Modal listing every toolbar / palette / panel icon in the editor with a
 *  short description, so users (especially on mobile, where hover tooltips
 *  don't apply) can discover what each control does. */
export function IconLegendDialog({ onClose }: IconLegendDialogProps) {
  // Close on Escape — matches the rest of the editor's modals.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <h2 className="text-base font-semibold">Icon reference</h2>
          <button
            type="button"
            aria-label="Close"
            className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-3 space-y-5">
          {ICON_LEGEND_GROUPS.map((group) => (
            <section key={group.title} className="space-y-2">
              <h3 className="text-xs font-medium uppercase text-muted-foreground tracking-wide">
                {group.title}
              </h3>
              <ul className="space-y-2">
                {group.entries.map((entry, i) => {
                  const Icon = entry.icon;
                  return (
                    <li
                      key={`${group.title}-${entry.name}-${i}`}
                      className="flex items-start gap-3"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent text-foreground shrink-0">
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="text-sm flex-1">
                        <div className="font-medium">{entry.name}</div>
                        <div className="text-xs text-muted-foreground">{entry.description}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
